import { mergeAttributes, Node } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import axios from 'axios';

/**
 * Image extension (custom wrapper) providing:
 * - Attributes: src (required), alt, title, width, height, class, loading
 * - Commands:
 *   - setImage({ src, alt?, title?, width?, height?, class? })
 *   - updateImageAttrs(attrs) – updates attributes of selected image node
 *   - removeImage() – deletes currently selected image node
 * - Paste / Drag & Drop handlers for files:
 *   1. Immediately inserts a temporary data URL for responsiveness.
 *   2. Uploads the file to /api/inline-images and replaces the data URL
 *      with a proper signed URL once the upload completes.
 * - Visual resize handle (bottom-right square). Drag horizontally to adjust width in pixels (persisted as numeric 'width' attr). Height auto by aspect.
 */

export interface ImageAttributes {
  src: string;
  alt?: string | null;
  title?: string | null;
  width?: string | null; // store as css value (e.g. '300px' or '50%')
  height?: string | null;
  class?: string | null;
  loading?: 'lazy' | 'eager' | null;
}

declare module '@tiptap/core' {

  // eslint-disable-next-line unused-imports/no-unused-vars
  interface Commands<ReturnType> {
    customImage: {
      /** Insert a new image */
      setImage: (_options: ImageAttributes) => ReturnType;
      /** Update currently selected image (if selection is on image) */
      updateImageAttrs: (_attrs: Partial<ImageAttributes>) => ReturnType;
      /** Remove currently selected image */
      removeImage: () => ReturnType;
    };
  }
}

export interface CustomImageOptions {
  HTMLAttributes: Record<string, any>;
  /** Whether images behave as block (default) or inline */
  inline: boolean;
  /** Allow base64 data URLs (enabled for quick insertion) */
  allowBase64: boolean;
}

export const CustomImage = Node.create<CustomImageOptions>({
  name: 'image',

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  inline() {
    return this.options.inline;
  },

  draggable: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      inline: false,
      allowBase64: true,
    } satisfies CustomImageOptions;
  },

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      class: { default: null },
      loading: { default: 'lazy' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { width, height, class: klass, ...rest } = HTMLAttributes;

    // width / height if set go as attributes; we don't embed style to keep it clean
    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, rest, {
        ...(klass ? { class: klass } : {}),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      }),
    ];
  },

  addCommands() {
    return {
      setImage: attrs => ({ chain }) => {
        if (!attrs?.src) {
          return false;
        }
        return chain().insertContent({ type: this.name, attrs }).run();
      },
      updateImageAttrs: attrs => ({ state, chain }) => {
        const { selection } = state;
        if (selection instanceof NodeSelection && selection.node.type.name === this.name) {
          return chain().updateAttributes(this.name, attrs).run();
        }
        return false;
      },
      removeImage: () => ({ state, tr, dispatch }) => {
        const { selection } = state;
        if (selection instanceof NodeSelection && selection.node.type.name === this.name) {
          if (dispatch) {
            dispatch(tr.delete(selection.from, selection.to));
          }
          return true;
        }
        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    const plugin = new Plugin({
      key: new PluginKey('customImagePasteDrop'),
      props: {
        handlePaste: (view, event) => {
          if (!event.clipboardData) {
            return false;
          }
          const items = Array.from(event.clipboardData.items || []);
          const file = items.map(i => (i.kind === 'file' ? i.getAsFile() : null)).find(Boolean);
          if (!file) {
            return false;
          }
          if (!file.type.startsWith('image/')) {
            return false;
          }
          // Insert a data URL placeholder immediately, then replace with the signed URL
          readFileAsDataURL(file).then((tempSrc) => {
            this.editor.commands.setImage({ src: tempSrc, alt: file.name, title: file.name });
            uploadInlineImage(file).then((signedUrl) => {
              replaceDataUrlInEditor(this.editor, tempSrc, signedUrl);
            }).catch(() => {
              // Upload failed: the data URL remains as a fallback so content is not lost
            });
          });
          return true; // we handled it
        },
        handleDrop: (view, event, _slice, moved) => {
          if (moved) {
            return false;
          }
          const dt = event.dataTransfer;
          if (!dt) {
            return false;
          }
          const file = Array.from(dt.files || []).find(f => f.type.startsWith('image/'));
          if (!file) {
            return false;
          }
          readFileAsDataURL(file).then((tempSrc) => {
            // place at drop position
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
            if (coordinates) {
              this.editor.chain().focus().insertContentAt(coordinates.pos, { type: this.name, attrs: { src: tempSrc, alt: file.name, title: file.name } }).run();
            }
            uploadInlineImage(file).then((signedUrl) => {
              replaceDataUrlInEditor(this.editor, tempSrc, signedUrl);
            }).catch(() => {
              // Upload failed: the data URL remains as a fallback so content is not lost
            });
          });
          return true;
        },
      },
    });
    return [plugin];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const outer = document.createElement('span');
      outer.classList.add('tiptap-image-wrapper');
      if (!editor.isEditable) {
        outer.classList.add('is-readonly');
      }

      const img = document.createElement('img');
      img.draggable = false;
      img.src = node.attrs.src;
      if (node.attrs.alt) {
        img.alt = node.attrs.alt;
      }
      if (node.attrs.title) {
        img.title = node.attrs.title;
      }
      if (node.attrs.loading) {
        img.loading = node.attrs.loading;
      }
      if (node.attrs.class) {
        img.className = node.attrs.class;
      }
      if (node.attrs.width) {
        img.setAttribute('width', node.attrs.width);
      }
      if (node.attrs.height) {
        img.setAttribute('height', node.attrs.height);
      }

      const handle = document.createElement('span');
      handle.classList.add('tiptap-image-resize-handle');
      handle.title = 'Drag to resize';

      outer.appendChild(img);
      outer.appendChild(handle);

      // Resize logic
      let startX = 0;
      let startWidth = 0;
      let dragging = false;

      const minWidth = 40; // px
      const maxWidth = 1200; // guard

      function startDrag(e: MouseEvent) {
        if (!editor.isEditable) {
          return;
        }
        e.preventDefault();
        dragging = true;
        startX = e.clientX;
        startWidth = img.getBoundingClientRect().width;
        document.addEventListener('mousemove', onDrag);
        document.addEventListener('mouseup', endDrag);
        outer.classList.add('resizing');
      }
      function onDrag(e: MouseEvent) {
        if (!dragging)
          return;
        const diff = e.clientX - startX;
        let newWidth = Math.round(startWidth + diff);
        if (newWidth < minWidth) {
          newWidth = minWidth;
        }
        if (newWidth > maxWidth) {
          newWidth = maxWidth;
        }
        img.style.width = `${newWidth}px`;
      }
      function endDrag() {
        if (!dragging)
          return;
        dragging = false;
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', endDrag);
        outer.classList.remove('resizing');
        const widthPx = Number.parseInt(img.style.width, 10);
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (pos != null) {
            editor.chain().focus().command(({ tr }) => {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                width: String(widthPx),
              });
              return true;
            }).run();
          }
        }
      }

      handle.addEventListener('mousedown', startDrag);

      // Selection styling
      function updateSelection() {
        const { state } = editor;
        const sel = state.selection;
        if (sel instanceof NodeSelection && typeof getPos === 'function') {
          const pos = getPos();
          if (pos === sel.from) {
            outer.classList.add('selected');
            return;
          }
        }
        outer.classList.remove('selected');
      }

      updateSelection();
      // TipTap's editor.on returns the editor instance; we store callback and won't attempt unsafe call.
      editor.on('selectionUpdate', updateSelection);

      return {
        dom: outer,
        destroy() {
          // TipTap 3 currently lacks an off helper; rely on GC.
          handle.removeEventListener('mousedown', startDrag);
        },
        update(updatedNode) {
          if (updatedNode.type.name !== this.name) {
            return false;
          }
          // Update attributes if changed externally
          if (updatedNode.attrs.src !== img.src) {
            img.src = updatedNode.attrs.src;
          }
          ['alt', 'title', 'loading'].forEach(attr => {
            if (updatedNode.attrs[attr]) {
              img.setAttribute(attr, updatedNode.attrs[attr]);
            }
          });
          if (updatedNode.attrs.class) {
            img.className = updatedNode.attrs.class;
          }
          if (updatedNode.attrs.width) {
            img.style.width = updatedNode.attrs.width.match(/^\d+$/) ? `${updatedNode.attrs.width}px` : updatedNode.attrs.width;
          }
          return true;
        },
      };
    };
  },
});

export default CustomImage;

// Utility outside of extension config so TS doesn't complain
function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a File to the inline-images API endpoint and return the signed URL.
 */
async function uploadInlineImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);
  const response = await axios.post<{ data: { url: string } }>('/api/inline-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data.url;
}

/**
 * Walk the editor document and replace every image node whose `src` equals
 * `tempSrc` with `newSrc`. This swaps out the local data URL for the
 * server-side signed URL returned after a successful upload.
 */
function replaceDataUrlInEditor(editor: import('@tiptap/core').Editor, tempSrc: string, newSrc: string): void {
  const { state, view } = editor;
  const tr = state.tr;
  let changed = false;

  state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.src === tempSrc) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: newSrc });
      changed = true;
    }
  });

  if (changed) {
    view.dispatch(tr);
  }
}
